/**
 * What a member has decided arrives on their own phone.
 *
 * MSG-3 (0122). Until this existed, a member could only be unsubscribed
 * by ringing the office — and a club that can only be unsubscribed from
 * by ringing the office is a club that gets marked as spam instead.
 *
 * Three things the screen has to be honest about, and all three come
 * from the database rather than being decided here.
 *
 * Safety is not a switch. It is shown, with the reason, and it has no
 * toggle — because a toggle wired to nothing is worse than no toggle:
 * somebody would believe it. `can_change` is a column, so there is one
 * place that knows.
 *
 * A channel the club cannot use is shown as unavailable rather than
 * offered. Turning on a text message from a club with no carrier changes
 * nothing, and a screen that pretends otherwise is lying about reach.
 *
 * And a channel with nowhere to send is called out. Somebody switching
 * everything on with no mobile number on file has changed nothing at
 * all, and should be told that rather than left to find out in a storm.
 */

import { useState } from 'react';

import { api } from '../lib/api.ts';
import { useApi, useMutation } from '../lib/useApi.ts';
import { Card, ErrorBanner, Loading, Status } from './ui.tsx';

interface Preference {
  topic: string;
  channel: string;
  allowed: boolean;
  is_default: boolean;
  can_change: boolean;
  why_not: string | null;
  label: string;
  description: string;
}

interface Payload {
  preferences: Preference[];
  channels: { sms: boolean; email: boolean };
  contact: { phone: string | null; email: string | null };
}

export function ContactPreferences() {
  const [reload, setReload] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const q = useApi<Payload>('/v1/me/contact-preferences', [reload]);

  const set = useMutation(async (v: { topic: string; channel: string; allowed: boolean }) => {
    setBusy(`${v.topic}:${v.channel}`);
    try {
      await api('/v1/me/contact-preferences', { method: 'PUT', body: v });
      setReload((n) => n + 1);
    } finally {
      setBusy(null);
    }
  });

  const d = q.data;
  if (q.loading && !d) return <Card title="How the Club Contacts You"><Loading /></Card>;
  if (!d) return <Card title="How the Club Contacts You"><ErrorBanner error={q.error} /></Card>;

  // One row per topic, with a cell per channel.
  const topics: { topic: string; label: string; description: string;
    can_change: boolean; why_not: string | null;
    by: Record<string, Preference> }[] = [];

  for (const p of d.preferences) {
    let row = topics.find((t) => t.topic === p.topic);
    if (!row) {
      row = {
        topic: p.topic, label: p.label, description: p.description,
        can_change: p.can_change, why_not: p.why_not, by: {},
      };
      topics.push(row);
    }
    row.by[p.channel] = p;
  }

  const missing = (channel: string) =>
    (channel === 'sms' && !d.contact.phone) || (channel === 'email' && !d.contact.email);

  return (
    <Card title="How the Club Contacts You">
      <ErrorBanner error={q.error ?? set.error} />

      <p className="muted small">
        Change any of these whenever you like. They take effect immediately — including
        for anything already waiting to be sent.
      </p>

      {(!d.channels.sms || !d.channels.email) && (
        <p className="hint">
          {/* Not a fault, and it should not read as one. */}
          The club has not set up
          {!d.channels.sms && !d.channels.email
            ? ' text messages or email'
            : !d.channels.sms ? ' text messages' : ' email'}
          {' '}yet, so nothing will arrive that way whatever these say.
        </p>
      )}

      {/* `data--cards` because at 390px the two toggle columns scroll
          off the right-hand edge — and the toggles are the entire point
          of the screen. Same treatment the member roster and the staff
          list use, and for the same reason: each row here is a THING the
          member is deciding about, not a step in a sequence. */}
      <table className="data data--cards">
        <thead>
          <tr>
            <th>What</th>
            <th className="num">Email</th>
            <th className="num">Text</th>
          </tr>
        </thead>
        <tbody>
          {topics.map((t) => (
            <tr key={t.topic}>
              <td data-label="What">
                {t.label}
                <div className="hint">{t.description}</div>
                {/* The reason, on the row, for the one they cannot change. */}
                {t.why_not && <div className="hint">{t.why_not}</div>}
              </td>
              {['email', 'sms'].map((ch) => {
                const p = t.by[ch];
                const label = ch === 'sms' ? 'Text' : 'Email';
                if (!p) return <td key={ch} className="num" data-label={label}>—</td>;

                if (!p.can_change) {
                  return (
                    <td key={ch} className="num" data-label={label}>
                      <Status tone="good">Always</Status>
                    </td>
                  );
                }

                return (
                  <td key={ch} className="num" data-label={label}>
                    <label className="row"
                      style={{ gap: 6, justifyContent: 'flex-end', marginBottom: 0 }}>
                      <input
                        type="checkbox"
                        style={{ width: 'auto', flex: '0 0 auto' }}
                        checked={p.allowed}
                        disabled={busy === `${t.topic}:${ch}`}
                        onChange={(e) => set.run({
                          topic: t.topic, channel: ch, allowed: e.target.checked,
                        })}
                      />
                    </label>
                    {p.allowed && missing(ch) && (
                      // Switched on with nowhere to send. Better said here
                      // than discovered in a storm.
                      <div className="hint">
                        no {ch === 'sms' ? 'number' : 'address'} on file
                      </div>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      <p className="hint">
        Safety messages — the course closing, lightning — are always sent, on every
        channel the club has for you. The only thing that stops one is a number the club
        does not have.
      </p>
    </Card>
  );
}
